// dibbitmap.ts ↔ game/bitmap.cpp DibBitmap (the web build's GPU-texture render
// surface) — CPU REFERENCE implementation.
//
// The C++ web renderer draws into SDL target textures with three blend states;
// this surface reproduces the GL math in floats and quantizes on write
// (round-to-nearest, the GL fixed-point conversion):
//   Blt          BLENDMODE_BLEND  out = src*sa + dst*(1-sa); outA = sa + dstA*(1-sa)
//   BltTinted    ColorMod + ADD   out = clamp(dst + src*(mod/255)*sa)   (team decals)
//   BltModulated ColorMod + BLEND out = src*(mod/255)*sa + dst*(1-sa)   (glyph recolor)
// Atlas art is overwhelmingly binary-alpha, where BLEND degenerates to copy/skip
// exactly; fractional-alpha edges (fog feather, shadows) are where the
// static-frame oracle arbitrates any ±1 GPU quantization drift (plan M2 risk).
//
// This is the ORACLE surface (and the fallback renderer); the shipping WebGL2
// path must match it pixel-for-pixel (integer-aligned nearest blits only).

import type { AtlasImage, Rect, Color, DibBitmapLike } from './texatlasmgr.ts';

export interface PixelSource {
  cx: number;
  cy: number;
  rgba: Uint8Array;
}

const q = (f: number): number => {
  const v = Math.round(f);
  return v < 0 ? 0 : v > 255 ? 255 : v;
};

export class CpuDibBitmap implements DibBitmapLike {
  cx: number;
  cy: number;
  rgba: Uint8Array;

  constructor(cx: number, cy: number, rgba?: Uint8Array) {
    this.cx = cx;
    this.cy = cy;
    this.rgba = rgba ?? new Uint8Array(cx * cy * 4);
  }

  GetSize(): { cx: number; cy: number } {
    return { cx: this.cx, cy: this.cy };
  }
  Width(): number {
    return this.cx;
  }
  Height(): number {
    return this.cy;
  }

  /** Shared blit core: per-pixel op over the clipped src/dst intersection.
   *  SDL_RenderCopy semantics at 1:1 scale — the dst rect mirrors the src rect's
   *  size; clipping happens against both surfaces' bounds. */
  private blitCore(
    src: PixelSource,
    prcSrc: Rect | null,
    xDst: number,
    yDst: number,
    op: (sr: number, sg: number, sb: number, sa: number, di: number) => void,
  ): void {
    const sl = prcSrc ? prcSrc.left : 0;
    const st = prcSrc ? prcSrc.top : 0;
    const sw = prcSrc ? prcSrc.right - prcSrc.left : src.cx;
    const sh = prcSrc ? prcSrc.bottom - prcSrc.top : src.cy;
    for (let y = 0; y < sh; y++) {
      const sy = st + y;
      const dy = yDst + y;
      if (sy < 0 || sy >= src.cy || dy < 0 || dy >= this.cy) continue;
      for (let x = 0; x < sw; x++) {
        const sx = sl + x;
        const dx = xDst + x;
        if (sx < 0 || sx >= src.cx || dx < 0 || dx >= this.cx) continue;
        const si = (sy * src.cx + sx) * 4;
        op(
          src.rgba[si],
          src.rgba[si + 1],
          src.rgba[si + 2],
          src.rgba[si + 3],
          (dy * this.cx + dx) * 4,
        );
      }
    }
  }

  // bitmap.cpp:220-245 — BLENDMODE_BLEND copy. `opacity` (0-255) fades the blit (cosmetic
  // per-sprite alpha); 255 keeps the original opaque fast path byte-for-byte.
  Blt(
    src: AtlasImage | PixelSource,
    prcSrc: Rect | null,
    xDst: number,
    yDst: number,
    opacity = 255,
  ): void {
    const d = this.rgba;
    if (opacity >= 255) {
      this.blitCore(src, prcSrc, xDst, yDst, (sr, sg, sb, sa, di) => {
        if (sa === 255) {
          d[di] = sr;
          d[di + 1] = sg;
          d[di + 2] = sb;
          d[di + 3] = 255;
        } else if (sa !== 0) {
          const a = sa / 255;
          d[di] = q(sr * a + d[di] * (1 - a));
          d[di + 1] = q(sg * a + d[di + 1] * (1 - a));
          d[di + 2] = q(sb * a + d[di + 2] * (1 - a));
          d[di + 3] = q(sa + d[di + 3] * (1 - a));
        }
      });
      return;
    }
    const o = opacity / 255;
    this.blitCore(src, prcSrc, xDst, yDst, (sr, sg, sb, sa, di) => {
      if (sa === 0) return;
      const a = (sa / 255) * o; // fade the effective coverage; blends with the real dst
      d[di] = q(sr * a + d[di] * (1 - a));
      d[di + 1] = q(sg * a + d[di + 1] * (1 - a));
      d[di + 2] = q(sb * a + d[di + 2] * (1 - a));
      d[di + 3] = q(sa * o + d[di + 3] * (1 - a));
    });
  }

  // bitmap.cpp:250-276 — ColorMod + BLENDMODE_ADD (team-color decals)
  BltTinted(
    src: AtlasImage | PixelSource,
    prcSrc: Rect | null,
    xDst: number,
    yDst: number,
    clr: Color,
    opacity = 255,
  ): void {
    const d = this.rgba;
    const o = opacity / 255; // 1.0 → byte-identical to the original additive blit
    const mr = clr.r / 255,
      mg = clr.g / 255,
      mb = clr.b / 255;
    this.blitCore(src, prcSrc, xDst, yDst, (sr, sg, sb, sa, di) => {
      if (sa === 0) return;
      const a = (sa / 255) * o;
      d[di] = q(d[di] + sr * mr * a);
      d[di + 1] = q(d[di + 1] + sg * mg * a);
      d[di + 2] = q(d[di + 2] + sb * mb * a);
      // GL_SRC_ALPHA/GL_ONE applies to alpha too: dstA + srcA*srcA
      d[di + 3] = q(d[di + 3] + sa * a);
    });
  }

  // texatlasmgr.cpp:326-344 — ColorMod + BLENDMODE_BLEND (glyph recolor)
  BltModulated(
    src: AtlasImage | PixelSource,
    prcSrc: Rect | null,
    xDst: number,
    yDst: number,
    clr: Color,
  ): void {
    const d = this.rgba;
    const mr = clr.r / 255,
      mg = clr.g / 255,
      mb = clr.b / 255;
    this.blitCore(src, prcSrc, xDst, yDst, (sr, sg, sb, sa, di) => {
      if (sa === 0) return;
      const a = sa / 255;
      d[di] = q(sr * mr * a + d[di] * (1 - a));
      d[di + 1] = q(sg * mg * a + d[di + 1] * (1 - a));
      d[di + 2] = q(sb * mb * a + d[di + 2] * (1 - a));
      d[di + 3] = q(sa + d[di + 3] * (1 - a));
    });
  }

  // bitmap.cpp:294-307 — opaque rect (SDL_RenderFillRect, draw color alpha 255)
  Fill(x: number, y: number, cx: number, cy: number, clr: Color): void {
    const x0 = Math.max(0, x),
      y0 = Math.max(0, y);
    const x1 = Math.min(this.cx, x + cx),
      y1 = Math.min(this.cy, y + cy);
    for (let yy = y0; yy < y1; yy++) {
      for (let xx = x0; xx < x1; xx++) {
        const i = (yy * this.cx + xx) * 4;
        this.rgba[i] = clr.r;
        this.rgba[i + 1] = clr.g;
        this.rgba[i + 2] = clr.b;
        this.rgba[i + 3] = 255;
      }
    }
  }

  // bitmap.cpp:347-350
  Clear(clr: Color): void {
    this.Fill(0, 0, this.cx, this.cy, clr);
  }

  // bitmap.cpp:352-367 — a black rect at AlphaMod 100, BLEND
  Shadow(x: number, y: number, cx: number, cy: number): void {
    const a = 100 / 255;
    const x0 = Math.max(0, x),
      y0 = Math.max(0, y);
    const x1 = Math.min(this.cx, x + cx),
      y1 = Math.min(this.cy, y + cy);
    for (let yy = y0; yy < y1; yy++) {
      for (let xx = x0; xx < x1; xx++) {
        const i = (yy * this.cx + xx) * 4;
        this.rgba[i] = q(this.rgba[i] * (1 - a));
        this.rgba[i + 1] = q(this.rgba[i + 1] * (1 - a));
        this.rgba[i + 2] = q(this.rgba[i + 2] * (1 - a));
        this.rgba[i + 3] = q(100 + this.rgba[i + 3] * (1 - a));
      }
    }
  }

  // bitmap.cpp:369-376 — SDL_RenderDrawLine: inclusive endpoints. UI usage is
  // axis-aligned (exact); diagonal rasterization parity is an oracle item.
  DrawLine(x1: number, y1: number, x2: number, y2: number, clr: Color): void {
    const dx = Math.abs(x2 - x1),
      dy = Math.abs(y2 - y1);
    const sx = x1 < x2 ? 1 : -1,
      sy = y1 < y2 ? 1 : -1;
    let err = dx - dy;
    let x = x1,
      y = y1;
    for (;;) {
      if (x >= 0 && x < this.cx && y >= 0 && y < this.cy) {
        const i = (y * this.cx + x) * 4;
        this.rgba[i] = clr.r;
        this.rgba[i + 1] = clr.g;
        this.rgba[i + 2] = clr.b;
        this.rgba[i + 3] = 255;
      }
      if (x === x2 && y === y2) break;
      const e2 = 2 * err;
      if (e2 > -dy) {
        err -= dy;
        x += sx;
      }
      if (e2 < dx) {
        err += dx;
        y += sy;
      }
    }
  }

  // bitmap.cpp:378-384 — self-copy (used by map scrolling); SDL copies through
  // a GPU pass, so overlapping regions read PRE-copy pixels: snapshot the source
  Scroll(prcSrc: Rect, xDst: number, yDst: number): void {
    const snap: PixelSource = { cx: this.cx, cy: this.cy, rgba: this.rgba.slice() };
    this.Blt(snap, prcSrc, xDst, yDst);
  }

  // bitmap.cpp:278-286 — copy only the tiles the UpdateMap says are invalid
  BltTiles(
    pbmSrc: CpuDibBitmap,
    pupd: { EnumUpdateRects(fFirst: boolean, prcBounds: Rect | null): Rect | null },
    yTopDst: number,
  ): void {
    let fFirst = true;
    for (;;) {
      const rc = pupd.EnumUpdateRects(fFirst, null);
      if (rc === null) break;
      fFirst = false;
      this.Blt(pbmSrc, rc, rc.left, rc.top + yTopDst);
    }
  }

  // bitmap.cpp:386-392 — full-width horizontal band view
  Suballoc(yTop: number, cy: number): SubBitmap;
  // bitmap.cpp:394-411 — rect view (only rc.left/rc.top offset writes; the
  // C++ SubBitmap shares the parent texture)
  Suballoc(rc: Rect): SubBitmap;
  Suballoc(a: number | Rect, cy?: number): SubBitmap {
    if (typeof a === 'number') {
      return new SubBitmap(this, { left: 0, top: a, right: this.cx, bottom: a + cy! });
    }
    return new SubBitmap(this, a);
  }

  // bitmap.cpp:320-345 — tile this bitmap over a destination box
  FillTo(pbmDst: CpuDibBitmap, xDst: number, yDst: number, cxDst: number, cyDst: number): void {
    for (let y = 0; y < cyDst; y++) {
      if (y % this.cy) continue;
      for (let x = 0; x < cxDst; x++) {
        if (x % this.cx) continue;
        const rc: Rect = {
          left: 0,
          top: 0,
          right: Math.min(this.cx, cxDst - x),
          bottom: Math.min(this.cy, cyDst - y),
        };
        pbmDst.Blt(this, rc, xDst + x, yDst + y);
      }
    }
  }
}

// bitmap.cpp:446-470 — SubBitmap: a positioned view onto the parent's pixels.
// GetSize reports the sub rect; every pixel op offsets by (m_rc.left, m_rc.top)
// and lands in the PARENT (the shared back-buffer texture). The web client
// partitions its back dib this way (game.cpp:1345/1364 sim + input strips).
export class SubBitmap extends CpuDibBitmap {
  private readonly parent: CpuDibBitmap;
  private readonly xOff: number;
  private readonly yOff: number;

  constructor(parent: CpuDibBitmap, rc: Rect) {
    super(rc.right - rc.left, rc.bottom - rc.top, new Uint8Array(0));
    this.parent = parent;
    this.xOff = rc.left;
    this.yOff = rc.top;
  }

  /** Renderer seam (WebGL2 source mapping): the view's parent + offset.
   *  Pure accessor — no pixel-path use. */
  viewOf(): { parent: CpuDibBitmap; x: number; y: number } {
    return { parent: this.parent, x: this.xOff, y: this.yOff };
  }

  override Blt(
    src: AtlasImage | PixelSource,
    prcSrc: Rect | null,
    xDst: number,
    yDst: number,
  ): void {
    this.parent.Blt(src, prcSrc, xDst + this.xOff, yDst + this.yOff);
  }

  override BltTinted(
    src: AtlasImage | PixelSource,
    prcSrc: Rect | null,
    xDst: number,
    yDst: number,
    clr: Color,
  ): void {
    this.parent.BltTinted(src, prcSrc, xDst + this.xOff, yDst + this.yOff, clr);
  }

  override BltModulated(
    src: AtlasImage | PixelSource,
    prcSrc: Rect | null,
    xDst: number,
    yDst: number,
    clr: Color,
  ): void {
    this.parent.BltModulated(src, prcSrc, xDst + this.xOff, yDst + this.yOff, clr);
  }

  override Fill(x: number, y: number, cx: number, cy: number, clr: Color): void {
    this.parent.Fill(x + this.xOff, y + this.yOff, cx, cy, clr);
  }

  // bitmap.cpp:457-460 — SubBitmap::Clear fills the sub rect on the parent
  override Clear(clr: Color): void {
    this.parent.Fill(this.xOff, this.yOff, this.cx, this.cy, clr);
  }

  override Shadow(x: number, y: number, cx: number, cy: number): void {
    this.parent.Shadow(x + this.xOff, y + this.yOff, cx, cy);
  }

  override DrawLine(x1: number, y1: number, x2: number, y2: number, clr: Color): void {
    this.parent.DrawLine(x1 + this.xOff, y1 + this.yOff, x2 + this.xOff, y2 + this.yOff, clr);
  }

  override Scroll(prcSrc: Rect, xDst: number, yDst: number): void {
    const rc: Rect = {
      left: prcSrc.left + this.xOff,
      top: prcSrc.top + this.yOff,
      right: prcSrc.right + this.xOff,
      bottom: prcSrc.bottom + this.yOff,
    };
    this.parent.Scroll(rc, xDst + this.xOff, yDst + this.yOff);
  }
}
